/**
 * V2 Chip — filter chip from design_reference/styles/app.css (`.chip`).
 * Supports an `on` selected state (accent-soft fill) and an optional count
 * shown in mono. Used by the dashboard source filter (All/Web/Mobile/…).
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../../../lib/utils';

/** Props for the V2 filter Chip. */
export interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Selected state. */
  on?: boolean;
  /** Optional count rendered on the right in mono. */
  count?: number;
  children: ReactNode;
}

/** V2 filter chip. */
export function Chip({ on, count, children, className, type, ...props }: ChipProps) {
  return (
    <button
      type={type ?? 'button'}
      className={cn(
        'inline-flex items-center gap-[6px] h-7 px-[11px] rounded-[var(--r-full)]',
        'text-[12px] font-medium border outline-none',
        'focus-visible:ring-2 focus-visible:ring-[var(--accent-line)]',
        on
          ? 'bg-[var(--accent-soft)] border-[var(--accent-line)] text-[var(--tx-hi)]'
          : 'bg-[var(--bg-2)] border-[var(--bd)] text-[var(--tx-mid)] hover:bg-[var(--bg-3)] hover:text-[var(--tx-hi)]',
        className,
      )}
      {...props}
    >
      {children}
      {count !== undefined && (
        <span
          className={cn(
            'font-[var(--font-mono)] text-[11px]',
            on ? 'text-[var(--accent-ink)]' : 'text-[var(--tx-faint)]',
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}
